/**
 * Bidirectional sync utilities between QuickFilterBar visual state and the text query.
 */

export function parseFiltersFromText(text, options) {
  const sectors = [];
  const metrics = [];
  if (!text) return { sectors, metrics };

  const lines = text.split(/\n/);
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    const clauses = trimmed.split(/\b(?:AND|OR)\b/i).map(c => c.trim()).filter(Boolean);

    for (const clause of clauses) {
      const sectorMatch = clause.match(/^Sector\s*=\s*(.+)$/i);
      if (sectorMatch) {
        const val = sectorMatch[1].trim();
        if (val && !sectors.includes(val)) sectors.push(val);
        continue;
      }

      const metricMatch = clause.match(/^(.+?)\s*([<>=!]+)\s*(.+)$/);
      if (metricMatch) {
        const name = metricMatch[1].trim();
        const condition = metricMatch[2].trim();
        const value = metricMatch[3].trim();
        const opt = options.find(o => o.shortName.toLowerCase() === name.toLowerCase());
        if (opt) {
          metrics.push({ shortName: opt.shortName, condition, value });
        }
      }
    }
  }
  return { sectors, metrics };
}

export function updateTextWithSectors(currentText, selectedSectors) {
  let text = currentText.replace(/\bSector\s*=\s*(?:(?!\b(?:AND|OR)\b)[^\n])+/gi, '');
  text = cleanConnectors(text);

  if (selectedSectors.length > 0) {
    const sectorClauses = selectedSectors.map(s => `Sector = ${s}`).join(' OR ');
    text = appendClause(text, sectorClauses);
  }
  return text;
}

export function updateTextWithMetric(currentText, shortName, condition, value, appendOnly = false) {
  let text = currentText;
  
  if (!appendOnly) {
    text = removeFilterLine(text, 'metric', shortName);
  }

  if (condition && value !== '' && value != null) {
    text = appendClause(text, `${shortName} ${condition} ${value}`);
  }
  return text;
}

export function removeFilterLine(currentText, type, value) {
  if (!currentText) return '';

  if (type === 'sector') {
    const escaped = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`\\bSector\\s*=\\s*${escaped}\\b(?:\\s*OR\\s*)?`, 'gi');
    let text = currentText.replace(pattern, '');
    return cleanConnectors(text);
  }
  
  const escaped = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`\\b${escaped}\\s*[<>=!]+\\s*(?:(?!\\b(?:AND|OR)\\b)[^\\n])+`, 'gi');
  
  let text = currentText.replace(pattern, '');
  return cleanConnectors(text);
}

// ─── Internal helpers ─────────────────────────────────────────

function appendClause(text, clause) {
  const trimmed = text.replace(/[\s\n]+$/, '');
  if (!trimmed) return clause;
  return trimmed + ' AND\n' + clause;
}

/**
 * Garbage collection for orphaned logical operators left behind by regex extractions.
 */
function cleanConnectors(text) {
  if (!text) return '';
  let result = text;
  
  // 1. Clean up dangling connectors inside parentheses (if user uses them)
  result = result.replace(/\(\s*(?:AND|OR)\s+/gi, '(');
  result = result.replace(/\s+(?:AND|OR)\s*\)/gi, ')');
  result = result.replace(/\(\s*\)/g, ''); 
  
  // 2. Collapse multiple consecutive AND/ORs into a single one.
  // We use a loop because global regex replacement doesn't handle overlapping 
  // consecutive sequences (e.g., "AND AND AND") in a single pass.
  let prev;
  do {
    prev = result;
    // Captures the first connector and replaces the whole block with it, 
    // preserving the surrounding whitespace and newlines naturally.
    result = result.replace(/\b(AND|OR)\s+(?:AND|OR)\b/gi, '$1');
  } while (result !== prev);
  
  // 3. Strip leading connectors (if the first metric was removed)
  result = result.replace(/^\s*(?:AND|OR)\b\s*/i, '');
  
  // 4. Strip trailing connectors (if the last metric was removed)
  result = result.replace(/\b(?:AND|OR)\s*$/i, '');
  
  // 5. Remove excess empty lines left behind by stripped clauses
  result = result.replace(/\n\s*\n+/g, '\n');
  
  return result.trim();
}

// /**
//  * Bidirectional sync utilities between QuickFilterBar visual state and the text query.
//  *
//  * Text→Visual: parseFiltersFromText() extracts structured filter objects from text
//  * Visual→Text: updateTextWith*() / removeFilterLine() modify the text query
//  */

// /**
//  * Parse the screener input text into structured filter objects.
//  * @param {string} text - The current query text (human-readable shortNames)
//  * @param {Array}  options - SCREENER_OPTIONS array
//  * @returns {{ sectors: string[], metrics: Array<{shortName:string, condition:string, value:string}> }}
//  */
// export function parseFiltersFromText(text, options) {
//   const sectors = [];
//   const metrics = [];
//   if (!text) return { sectors, metrics };

//   // Split into individual clauses: split newlines, then split AND/OR within lines
//   const lines = text.split(/\n/);
//   for (const line of lines) {
//     const trimmed = line.trim();
//     if (!trimmed) continue;

//     // Split by AND / OR connectors
//     const clauses = trimmed.split(/\b(?:AND|OR)\b/i).map(c => c.trim()).filter(Boolean);

//     for (const clause of clauses) {
//       // Sector pattern: "Sector = Technology"
//       const sectorMatch = clause.match(/^Sector\s*=\s*(.+)$/i);
//       if (sectorMatch) {
//         const val = sectorMatch[1].trim();
//         if (val && !sectors.includes(val)) sectors.push(val);
//         continue;
//       }

//       // Metric pattern: "ShortName <condition> value"
//       const metricMatch = clause.match(/^(.+?)\s*([<>=!]+)\s*(.+)$/);
//       if (metricMatch) {
//         const name = metricMatch[1].trim();
//         const condition = metricMatch[2].trim();
//         const value = metricMatch[3].trim();
//         // Verify the name is a known option (or "Market Cap" etc.)
//         const opt = options.find(o => o.shortName.toLowerCase() === name.toLowerCase());
//         if (opt) {
//           metrics.push({ shortName: opt.shortName, condition, value });
//         }
//       }
//     }
//   }
//   return { sectors, metrics };
// }

// /**
//  * Update query text with the given sector selections.
//  * Removes all existing Sector = ... clauses and appends new ones.
//  * @param {string}   currentText
//  * @param {string[]} selectedSectors
//  * @returns {string}
//  */
// export function updateTextWithSectors(currentText, selectedSectors) {
//   // Remove existing sector lines/clauses
//   let text = removeLinesMatching(currentText, /^\s*Sector\s*=\s*.+$/i);

//   // Append new sector clauses
//   if (selectedSectors.length > 0) {
//     const sectorClauses = selectedSectors.map(s => `Sector = ${s}`).join(' OR ');
//     text = appendClause(text, sectorClauses);
//   }
//   return text;
// }

// /**
//  * Update query text with a metric filter (add or replace existing).
//  * @param {string} currentText
//  * @param {string} shortName - e.g. "PE", "Market Cap"
//  * @param {string} condition - e.g. ">", "<", ">="
//  * @param {string} value
//  * @returns {string}
//  */
// export function updateTextWithMetric(currentText, shortName, condition, value) {
//   // Remove existing lines that start with this shortName
//   const escaped = shortName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
//   const pattern = new RegExp('^\\s*' + escaped + '\\s*[<>=!]+\\s*.+$', 'im');
//   let text = cleanConnectors(removeLinesMatching(currentText, pattern));

//   // Append the new metric clause
//   if (condition && value !== '' && value != null) {
//     text = appendClause(text, `${shortName} ${condition} ${value}`);
//   }
//   return text;
// }

// /**
//  * Remove a filter line from the text by filter key.
//  * @param {string} currentText
//  * @param {'sector'} type - filter type
//  * @param {string}   value - sector name or metric shortName
//  * @returns {string}
//  */
// export function removeFilterLine(currentText, type, value) {
//   if (type === 'sector') {
//     const escaped = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
//     // Remove "Sector = Value" clause; if it's part of an OR chain, remove just that part
//     let text = currentText.replace(
//       new RegExp('\\s*(?:OR\\s+)?Sector\\s*=\\s*' + escaped + '(?:\\s*OR\\s*)?', 'gi'),
//       ''
//     );
//     // Clean up if "Sector =" line is now empty
//     text = removeLinesMatching(text, /^\s*Sector\s*=\s*$/im);
//     return cleanConnectors(text);
//   }
//   // metric removal
//   const escaped = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
//   const pattern = new RegExp('^\\s*' + escaped + '\\s*[<>=!]+\\s*.+$', 'im');
//   return cleanConnectors(removeLinesMatching(currentText, pattern));
// }

// // ─── Internal helpers ─────────────────────────────────────────

// function removeLinesMatching(text, pattern) {
//   const lines = text.split(/\n/);
//   const filtered = lines.filter(line => !pattern.test(line));
//   return filtered.join('\n');
// }

// function appendClause(text, clause) {
//   const trimmed = text.replace(/[\s\n]+$/, '');
//   if (!trimmed) return clause;
//   return trimmed + ' AND\n' + clause;
// }

// function cleanConnectors(text) {
//   let result = text;
//   // Remove leading AND/OR
//   result = result.replace(/^\s*(AND|OR)\s*/i, '');
//   // Remove trailing AND/OR
//   result = result.replace(/\s*(AND|OR)\s*$/i, '');
//   // Remove double AND/OR (e.g. "AND\nAND")
//   result = result.replace(/(AND|OR)\s*\n\s*(AND|OR)/gi, '$1');
//   // Remove empty lines
//   result = result.replace(/\n{2,}/g, '\n');
//   return result.trim();
// }
